/* eslint-disable @typescript-eslint/no-magic-numbers */
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DialogBox4, { DialogBox4Reference } from '@/components/DialogBox4';
import Loader from '@/components/Loader';
import { useButtonDialogBox } from '@/hooks/useButtonDialogBox';
import useLanguage from '@/hooks/useLanguage';
import { AtHomeLoginResponse } from '@/services/api';

import StudentProfileScreen from './components/ProfileScreen';
import useProfileScreen from './hooks/useProfileScreen';

type ExpiryInfo = { value: number; unit: 'days' | 'hours' | 'minutes' };

const getExpiryDay = (): ExpiryInfo => {
  const response = localStorage.getItem('atHomeLoginResponse') || '';
  const atHomeLoginResponse = JSON.parse(response) as AtHomeLoginResponse;
  const refreshMs = atHomeLoginResponse?.refreshTokenExpiryTime
    ? new Date(atHomeLoginResponse.refreshTokenExpiryTime).getTime() - Date.now()
    : 0;

  const remainingMs = Math.min(refreshMs > 0 ? refreshMs : 0);

  if (remainingMs <= 0) {
    return { value: 0, unit: 'minutes' };
  }

  const totalMinutes = Math.floor(remainingMs / (1000 * 60));
  const totalHours = Math.floor(remainingMs / (1000 * 60 * 60));
  const totalDays = Math.floor(remainingMs / (1000 * 60 * 60 * 24));

  if (totalDays >= 1) return { value: totalDays, unit: 'days' };
  if (totalHours >= 1) return { value: totalHours, unit: 'hours' };
  return { value: totalMinutes, unit: 'minutes' };
};

const getAutoSignOutDate = () => {
  const response = localStorage.getItem('atHomeLoginResponse') || '';
  const atHomeLoginResponse = JSON.parse(response) as AtHomeLoginResponse;
  if (!atHomeLoginResponse?.refreshTokenExpiryTime) {
    return '';
  }
  const date = new Date(atHomeLoginResponse?.refreshTokenExpiryTime);
  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleDateString('en-US', { month: 'short' }).replace(' ', '');
  const formatted = `${weekday}, ${day}${month}`;
  return formatted;
};

function ProfileScreen() {
  const { studentProfileData, onResetApp } = useProfileScreen();
  const { showButtonDialogBox, closeDialog } = useButtonDialogBox();
  const [currentStudentIndex, setCurrentStudentIndex] = useState(0);
  const { t } = useTranslation(['atHome', 'studentLogin']);

  const dialogReference = useRef<DialogBox4Reference>(null);
  const onDismiss = useCallback(() => {
    if (dialogReference.current) dialogReference.current?.onDismiss();
  }, []);
  const {
    openLanguageDialog,
    isLanguageDialogOpen,
    dialogProperties: languageDialogProperties,
    isApplyingLanguage,
  } = useLanguage(onDismiss);

  const onLanguageClick = () => {
    openLanguageDialog();
  };

  const onResetClick = () => {
    showButtonDialogBox({
      open: true,
      title: t('atHome:resetApp'),
      subtitle: t('atHome:resetConfirmationText'),
      leftButtonText: t('studentLogin:cancelButton'),
      rightButtonText: t('studentLogin:continueButton'),
      onLeftButtonClick: closeDialog,
      onRightButtonClick: () => {
        onResetApp();
        closeDialog();
      },
      showCloseButton: false,
      variant: 'blue',
    });
  };

  const onSwitchProfile = () => {
    showButtonDialogBox({
      open: true,
      title: t('atHome:resetProfile'),
      subtitle: t('atHome:resetConfirmationText'),
      leftButtonText: t('studentLogin:cancelButton'),
      rightButtonText: t('studentLogin:continueButton'),
      onLeftButtonClick: closeDialog,
      onRightButtonClick: () => {
        onResetApp();
        closeDialog();
      },
      showCloseButton: false,
      variant: 'blue',
    });
  };

  return (
    <>
      <StudentProfileScreen
        expiryDay={getExpiryDay()}
        autoSignOutDate={getAutoSignOutDate()}
        studentProfileData={studentProfileData}
        onResetClick={onResetClick}
        onLanguageClick={onLanguageClick}
        currentStudentIndex={currentStudentIndex}
        setCurrentStudentIndex={setCurrentStudentIndex}
        onHandleSwitchProfile={onSwitchProfile}
      />
      {isLanguageDialogOpen && (
        <DialogBox4
          open={isLanguageDialogOpen}
          variant="blue"
          showCloseIcon={false}
          ref={dialogReference}
          onClose={languageDialogProperties.onClose}
        >
          {languageDialogProperties.component}
        </DialogBox4>
      )}
      {isApplyingLanguage && <Loader />}
    </>
  );
}

export default ProfileScreen;
